import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../useAxios';

// Create the context
const SiteDataContext = createContext();

// Custom hook for using site data context
export const useSiteData = () => useContext(SiteDataContext);

// Helper to generate or retrieve device ID
const getOrSetDeviceId = () => {
  let deviceId = localStorage.getItem('device_id');
  if (!deviceId) {
    deviceId = Math.random().toString(36).substring(2, 12);
    localStorage.setItem('device_id', deviceId);

    const expiry = new Date();
    expiry.setMonth(expiry.getMonth() + 1);
    localStorage.setItem('device_id_expiry', expiry.getTime());
  }
  return deviceId;
};

// SiteDataProvider component
export const SiteDataProvider = ({ children }) => {
  const navigate = useNavigate();
  
  // Initialize state with values from localStorage (or defaults)
  const [siteData, setSiteData] = useState(() => {
    const savedSettings = localStorage.getItem('siteSettings');
    const defaultSettings = {
      theme: 'light',
      currency: 'INR',
      notificationsEnabled: true,
      deviceId: getOrSetDeviceId(),
      cartCount: 0,
    };

    return savedSettings
      ? { ...defaultSettings, ...JSON.parse(savedSettings) }
      : defaultSettings;
  });

  // Fetch homepage settings from API on mount
  useEffect(() => {
    const fetchInitialSettings = async () => {
      try {
        const lastUpdatedOn = localStorage.getItem('siteDataUpdatedOn') || '0';
        const deviceId = siteData.deviceId;

        const res = await axiosInstance.post('/f91e-521e0e', { lastUpdatedOn, deviceId });
        const result = res?.data || {};

        if (result.updated_on && result.updated_on !== lastUpdatedOn) {
          localStorage.setItem('siteDataUpdatedOn', result.updated_on);
          // Merge all returned fields with existing state
          setSiteData(prevData => {
            const merged = { ...prevData, ...result };
            localStorage.setItem('siteSettings', JSON.stringify(merged));
            return merged;
          });
        }
      } catch (error) {
        console.error('Error fetching initial site settings:', error);
      }
    };

    fetchInitialSettings();
  }, []);

  // Listen for changes in localStorage and update state accordingly
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'siteSettings') {
        setSiteData(e.newValue ? JSON.parse(e.newValue) : {});
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Update site data and sync with localStorage
  const updateSiteData = (newSettings) => {
    const updatedSettings = { ...siteData, ...newSettings };
    setSiteData(updatedSettings);
    localStorage.setItem('siteSettings', JSON.stringify(updatedSettings));
  };

  // Getter for the device ID (useful for tracking, etc.)
  const getDeviceId = () => siteData.deviceId;

  // Logout or clear site data if necessary
  const clearSiteData = () => {
    setSiteData({
      theme: 'light',
      currency: 'INR',
      notificationsEnabled: true,
      deviceId: getOrSetDeviceId(),
      cartCount: 0,
    });
    localStorage.removeItem('siteSettings');
    navigate('/home');
  };

  return (
    <SiteDataContext.Provider value={{ siteData, updateSiteData, getDeviceId, clearSiteData }}>
      {children}
    </SiteDataContext.Provider>
  );
};
